/* eslint-disable import/order */
/* eslint-disable no-underscore-dangle */
import {useQuery, useLazyQuery} from '@apollo/client';
import {
  Box,
  FormControl,
  FormControlLabel,
  Checkbox,
  Select,
  MenuItem,
  Grid,
} from '@mui/material';
import {GridColDef} from '@mui/x-data-grid-pro';
import {Email} from '@mui/icons-material';
import React, {useEffect, useMemo, useState} from 'react';
import {Link, useNavigate} from 'react-router-dom';
import {capitalize} from 'lodash-es';
import {
  GET_ACCOUNTS,
  GET_LEADS_AND_ACCOUNTS,
} from '../../components/AccountOverview/accountOverviewQueries';
import {SearchResults} from '../../components/Common/SearchResults';
import {
  RenderColor,
  RenderColorBasic,
  RenderPropositionType,
} from '../../components/EditorGrid/GridRenderComponents';
import {
  contactTypeColors,
  divisionColors,
  propositionTypeColors,
  propositionTypeLabels,
} from '../../components/ContactPage/contactStyles';
import {useDebounce} from '../../hooks/useDebounce';
import {useRecentClients} from '../../hooks/useRecentClients';
import useTitle from '../../hooks/useTitle';
import {
  accounts,
  accountsVariables,
  accounts_accounts,
} from '../../schema-types/accounts';
import {
  LeadsAndAccounts,
  LeadsAndAccountsVariables,
  LeadsAndAccounts_leadsAndAccounts,
} from '../../schema-types/LeadsAndAccounts';
import {
  ContactSearchQuery,
  ContactSearchQueryVariables,
} from '../../schema-types/ContactSearchQuery';
import {ContactSearchFragment} from '../../schema-types/ContactSearchFragment';
import {darkBlue, themeColors} from '../../theme/Theme';
import {CONTACT_SEARCH_QUERY} from './contactQueries';
import {useDjangoContext} from '../../contexts/useAuthContext';
import {ShowIfDebugWrapper, useDebugContext} from '../../contexts/DebugProvider';
import {BusinessUnitEnum, GlobalPermissionEnum} from '../../schema-types/globalTypes';
import {renderPhone} from '../../utils/renderPhone';
import withAutoFormNew from '../../components/AutoForm/AutoFormNew';
import {FeatureBoundary} from '../../contexts/FeatureBoundary';
import {BusinessUnitSearch} from '../../components/BusinessUnitSearch/BusinessUnitSearch';
import {useAutoField} from '../../components/AutoForm/useAutoForm';
import {AccountSearchFragment} from '../../schema-types/AccountSearchFragment';
import {gridCellDef} from '../../utils/GridCellDef';
import {SearchInput} from './SearchInput';

type AccountColumnProps = GridColDef & {
  field: string;
};

export const RenderAccountNameAndEmail: gridCellDef<LeadsAndAccounts_leadsAndAccounts> = (
  params
) => {
  const account = params.row;
  const email =
    account.__typename === 'AccountType' ? account.primaryContact?.email : null;
  return (
    <div
      style={{
        flex: 1,
        justifyContent: 'center',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'flex-start',
        gap: 4,
        color: darkBlue,
        textDecoration: 'none',
        paddingLeft: 10,
        lineHeight: 1,
        height: '100%',
      }}
    >
      <Link
        className="sentry-mask"
        role="a"
        to={{
          pathname:
            account.__typename === 'LeadType'
              ? `/leads/${params.id}/basic`
              : `/account/${params.id}`,
        }}
        onClick={(e) => {
          e.stopPropagation();
        }}
        style={{fontWeight: 600, color: darkBlue, textDecoration: 'none'}}
      >
        <>{params.row.name || ''} </>
      </Link>
      {params.row.isDeleted ? '(inactive)' : ''}
      {email ? (
        <a
          className="sentry-mask"
          onClick={(e) => e.stopPropagation()} // disable row navigation event
          style={{
            color: darkBlue,
            textAlign: 'center',
            textDecoration: 'none',
            display: 'flex',
            alignItems: 'center',
          }}
          href={`mailto:${email}`}
        >
          <Email style={{marginRight: 4}} fontSize="small" />
          {email}
        </a>
      ) : null}
      <FeatureBoundary
        showErrorIfDenied={false}
        features={[GlobalPermissionEnum.can_restore_admin_permission]}
      >
        <ShowIfDebugWrapper>
          <FeatureBoundary
            features={[GlobalPermissionEnum.can_access_dentist_recommendation_builder]}
            showErrorIfDenied={false}
          >
            <Link
              to={{
                pathname: `/test/test-any-open-review/${account?.curoId}`,
                search: 'goTo=builder',
              }}
              style={{color: themeColors.coral, textDecoration: 'none'}}
            >
              ➡ Create builder
            </Link>
          </FeatureBoundary>
          {account.__typename === 'LeadType' ? null : (
            <>
              <Link
                onClick={(e) => {
                  e.stopPropagation();
                }}
                to={{
                  pathname: `/test/test-any-open-review/${account?.curoId}`,
                }}
                style={{color: themeColors.darkGreen, textDecoration: 'none'}}
              >
                ➡ Any open review
              </Link>
              <Link
                role="a"
                to={{
                  pathname:
                    account.businessUnit?.partnershipType === BusinessUnitEnum.DA
                      ? `/account/${params.id}/requirements`
                      : `/account/${params.id}/reviews`,
                }}
                onClick={(e) => {
                  e.stopPropagation();
                }}
                style={{color: themeColors.purple, textDecoration: 'none'}}
              >
                ➡ Go to{' '}
                {account.businessUnit?.partnershipType === BusinessUnitEnum.DA
                  ? 'instructions'
                  : 'reviews'}
              </Link>
              <Link
                to={{
                  pathname: `/test/test-any-open-review/${account?.curoId}`,
                  search: 'goTo=recommends-builder',
                }}
                style={{color: themeColors.coral, textDecoration: 'none'}}
              >
                ➡ Create{' '}
                {account.businessUnit?.partnershipType === BusinessUnitEnum.DA
                  ? 'instruction'
                  : 'recommendation'}
              </Link>
            </>
          )}
        </ShowIfDebugWrapper>
      </FeatureBoundary>
    </div>
  );
};

const accountFirstColumns: AccountColumnProps[] = [
  {
    field: 'name',
    headerName: ' ',
    flex: 2,
    renderCell: RenderAccountNameAndEmail,
  },
  {
    field: 'accountStatus',
    headerName: 'Status',
    flex: 0.7,
    align: 'center',
    valueGetter: (value, row, column, apiRef) => row.accountStatus || '',
    renderCell: RenderColor(contactTypeColors),
  },
];

const accountSaltusColumns: AccountColumnProps[] = [
  {
    field: 'business unit',
    headerName: 'Business unit',
    flex: 1.5,
    valueGetter: (value, row, column, apiRef) => {
      const this_row = row as accounts_accounts;
      return this_row.businessUnit?.name || '';
    },
    align: 'left',
  },
];

export const accountSecondColumns: AccountColumnProps[] = [
  {
    field: 'city',
    headerName: 'City',
    valueGetter: (value, row, column, apiRef) =>
      row.city || (row.addresses?.length ? capitalize(row.addresses[0].city) : null),
    flex: 0.7,
    align: 'center',
    headerAlign: 'left',
  },
  {
    field: 'adviser.name',
    headerName: 'Adviser',
    flex: 1,
    align: 'center',
    valueGetter: (value, row, column, apiRef) => row.adviser?.name,
  },
  {
    field: 'primaryContact' as any,
    headerName: 'Primary contact',
    valueGetter: (value, row, column, apiRef) => row.primaryContact?.name,
    flex: 1,
  },
  {
    field: 'secondaryContact' as any,
    headerName: 'Secondary contact',
    valueGetter: (value, row, column, apiRef) => row.secondaryContact?.name,
    flex: 1,
  },
  {
    renderCell: RenderPropositionType(propositionTypeColors, propositionTypeLabels),
    valueGetter: (value, row, column, apiRef) => row.propositionType,
    field: 'propositionType',
    headerName: 'Proposition Type',
    flex: 1,
  },
  {
    field: 'primaryPhone',
    headerName: 'Phone',
    valueGetter: (value, row, column, apiRef) =>
      row.primaryPhone || row.homePhone || row.mobilePhone,
    flex: 1,
  },
];

const devPreferredStartEmails: Record<string, string> = {
  // FEEL FREE TO ADD YOUR EMAIL IF YOU PREFER TO HAVE ACCOUNTS LOAD WITH PARTICULAR ACCOUNTS
  // 'saturnino.harris@saltus.co.uk': 'test account 7',
};

export const AccountContactSearchPage: React.FC = () => {
  return <AccountContactSearchWrapped formFields={[]} />;
};

type AccountContactSearchType = {
  businessUnitId: string;
};

export const AccountContactSearch: React.FC = () => {
  useTitle('Search');
  const navigate = useNavigate();
  const [{value: businessUnitId}] = useAutoField<string>('businessUnitId');
  const {djangoUser, perms} = useDjangoContext();
  const [searchType, setSearchType] = useState<'accounts' | 'contacts'>('accounts');
  const [search, setSearch] = useState<string>('');
  const {debug} = useDebugContext();
  const [includeLeads, setIncludeLeads] = useState(false);
  const {previousSearches} = useRecentClients();
  const delayedSearch = useDebounce(search, 1000);
  const previousSearchIdList: string[] = Array.from(
    new Set((previousSearches || []).filter((x) => x.curoId).map((x) => x.curoId))
  );
  const [
    getAccountsOnly,
    {loading: accountsLoading, error: accountsError, data: accountsData},
  ] = useLazyQuery<accounts, accountsVariables>(GET_ACCOUNTS);

  const [
    getAccountsAndLeads,
    {
      loading: accountsAndLeadsLoading,
      error: accountsAndLeadsError,
      data: accountsAndLeadsData,
    },
  ] = useLazyQuery<LeadsAndAccounts, LeadsAndAccountsVariables>(GET_LEADS_AND_ACCOUNTS);

  const {data, loading} = useQuery<ContactSearchQuery, ContactSearchQueryVariables>(
    CONTACT_SEARCH_QUERY,
    {
      variables: {
        search: delayedSearch,
      },
      skip: searchType !== 'contacts' || delayedSearch.length < 3,
    }
  );

  const contacts = data?.contactsSearch || [];

  useEffect(() => {
    if (searchType !== 'accounts') {
      return;
    }
    if (includeLeads) {
      getAccountsAndLeads({
        variables: {
          search: delayedSearch.length < 3 ? '' : delayedSearch,
        },
      });
    } else {
      getAccountsOnly({
        variables: {
          search: delayedSearch.length < 3 ? null : delayedSearch,
          curoIds: delayedSearch.length ? null : previousSearchIdList,
          businessUnitIds: businessUnitId ? [businessUnitId] : null,
        },
      });
    }
  }, [includeLeads, searchType, delayedSearch, businessUnitId]);

  const _accountSearchResults = includeLeads
    ? accountsAndLeadsData?.leadsAndAccounts
    : accountsData?.accounts;

  const sortedSearchResults = // if we're showing previous searches, show most recent at the top
    delayedSearch.length || !_accountSearchResults
      ? _accountSearchResults
      : [..._accountSearchResults].sort((a, b) => {
          const aIndex = previousSearchIdList.indexOf(a.curoId);
          const bIndex = previousSearchIdList.indexOf(b.curoId);
          return aIndex - bIndex;
        });

  const dynamicColumns = useMemo(() => {
    const columns = [...accountFirstColumns];
    if (debug?.forms) {
      columns.push({
        field: 'partnership',
        headerName: 'Partnership',
        flex: 1,
        valueGetter: (value, row, column, apiRef) => {
          const rowData = row as AccountSearchFragment;
          return rowData.businessUnit?.partnershipType?.toLocaleUpperCase() || null;
        },
        renderCell: RenderColor(divisionColors, true),
      });
    }
    if (
      perms.includes(GlobalPermissionEnum.can_access_limited_features) ||
      debug?.forms
    ) {
      columns.push(...accountSaltusColumns);
    }
    columns.push(...accountSecondColumns);
    return columns;
  }, [perms]);

  return (
    <>
      <Grid
        container
        paddingRight={3}
        mb={3}
        display="flex"
        flexDirection="row"
        alignItems="center"
        justifyContent="space-between"
        spacing={2}
      >
        <Grid item flex={1}>
          <Box bgcolor={themeColors.white} borderRadius="4px">
            <SearchInput setSearch={setSearch} search={search} />
          </Box>
        </Grid>

        {searchType === 'accounts' ? (
          <FeatureBoundary
            showErrorIfDenied={false}
            features={[GlobalPermissionEnum.can_access_admin_features]}
          >
            <Grid item xs={2}>
              <Box
                display="flex"
                justifyContent="stretch"
                alignContent="stretch"
                bgcolor={themeColors.white}
                borderRadius="4px"
              >
                <BusinessUnitSearch />
              </Box>
            </Grid>
          </FeatureBoundary>
        ) : null}
        <Grid item>
          <Box bgcolor={themeColors.white} borderRadius="4px">
            <FormControl>
              <Select
                onChange={(e) => setSearchType(e.target.value as any)}
                value={searchType}
              >
                <MenuItem value="accounts">Accounts</MenuItem>
                <MenuItem value="contacts">Contacts</MenuItem>
              </Select>
            </FormControl>
          </Box>
        </Grid>

        <Grid item style={{opacity: searchType === 'accounts' ? 1 : 0}}>
          <FormControl
            className="include-leads"
            sx={{alignContent: 'center', display: 'flex'}}
            onClick={() =>
              setIncludeLeads((currentlyIncludesLeads) => !currentlyIncludesLeads)
            }
          >
            <FormControlLabel
              label="Include leads?"
              control={<Checkbox checked={includeLeads || false} />}
            />
          </FormControl>
        </Grid>
      </Grid>
      {searchType === 'accounts' ? (
        <SearchResults
          dataGridId="AccountsSearch"
          key="accounts"
          title="Accounts"
          loading={includeLeads ? accountsAndLeadsLoading : accountsLoading}
          search={search}
          columns={dynamicColumns}
          data={sortedSearchResults || []}
          onRowDoubleClick={(params, event) => {
            const account = params.row as LeadsAndAccounts_leadsAndAccounts;
            if (account.__typename === 'LeadType') {
              navigate({pathname: `/leads/${params.id}/basic`});
            } else if ((event as any).altKey) {
              navigate({pathname: `/account/${params.id}/reviews`});
            } else {
              navigate({pathname: `/account/${params.id}`});
            }
          }}
        />
      ) : (
        <SearchResults
          dataGridId="ContactsSearch"
          key="contacts"
          title="Contacts"
          loading={loading}
          search={search}
          columns={contactSearchColumns}
          data={contacts}
          onRowDoubleClick={(params, event) => {
            navigate({pathname: `/contacts/${params.row.curoId}`});
          }}
        />
      )}
    </>
  );
};

export const RenderName: gridCellDef<ContactSearchFragment> = (params) => {
  return (
    <div
      style={{
        flex: 1,
        alignItems: 'center',
        justifyContent: 'space-between',
        display: 'flex',
        height: '100%',
        color: darkBlue,
        paddingLeft: 10,
        fontWeight: 600,
        textDecoration: 'none',
      }}
    >
      <>
        {params.row.name || ''} {params.row.isDeleted ? '(inactive)' : ''}
      </>
    </div>
  );
};

export const RenderContactNameAndEmail: gridCellDef<ContactSearchFragment> = (params) => {
  const {row} = params;
  const {
    row: {email},
  } = params;
  const isPrimary = row.accountContacts.some((x) => x.isPrimary);
  const importanceText = isPrimary ? 'Primary' : 'Secondary';
  return (
    <div
      style={{
        flex: 1,
        justifyContent: 'center',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'flex-start',
        gap: 4,
        paddingTop: 8,
        paddingBottom: 8,
        color: darkBlue,
        textDecoration: 'none',
        paddingLeft: 10,
        lineHeight: 1,
        height: '100%',
      }}
    >
      <Box display="flex" gap={5} alignItems="center" justifyContent="flex-start">
        <Link
          role="a"
          to={{pathname: `/contacts/${params.row.curoId}`}}
          onClick={(e) => {
            e.stopPropagation();
          }}
          style={{fontWeight: 600, color: darkBlue, textDecoration: 'none'}}
        >
          <>{params.row.name || ''} </>
        </Link>
        {RenderColorBasic(
          {Primary: themeColors.lightBlue, Secondary: themeColors.lightYellow},
          importanceText,
          importanceText,
          10
        )}
      </Box>

      {params.row.isDeleted ? '(inactive)' : ''}
      {email ? (
        <a
          onClick={(e) => e.stopPropagation()} // disable row navigation event
          style={{
            color: darkBlue,
            textAlign: 'center',
            textDecoration: 'none',
            display: 'flex',
            alignItems: 'center',
          }}
          href={`mailto:${email}`}
        >
          <Email style={{marginRight: 4}} fontSize="small" />
          {email}
        </a>
      ) : (
        <Box mt={2} />
      )}
    </div>
  );
};

export type ContactSearchColumnProps = GridColDef & {
  field: keyof ContactSearchFragment;
};
export const contactSearchColumns: ContactSearchColumnProps[] = [
  {
    field: 'name',
    headerName: ' ',
    flex: 2,
    renderCell: RenderContactNameAndEmail,
  },
  {
    field: 'contactStatus',
    headerName: 'Status',
    flex: 1,
    renderCell: RenderColor(contactTypeColors),
  },
  {
    field: 'accountContacts.proposition' as any,
    headerName: 'Proposition',
    flex: 1.5,
    valueGetter: (value, row, column, apiRef) => {
      const rowData = row as ContactSearchFragment;
      const account = rowData.accountContacts.find(
        (x) => !!x.account?.propositionType
      )?.account;
      return account?.propositionType;
    },
  },
  {
    field: 'primaryPhone',
    headerName: 'Phone',
    align: 'center',
    flex: 1,
    renderCell: renderPhone,
  },
  {
    field: 'homePhone',
    headerName: 'Home phone',
    align: 'center',
    flex: 1,
    hideSortIcons: true,
    renderCell: renderPhone,
  },
];

const AccountContactSearchWrapped = withAutoFormNew<AccountContactSearchType, object>(
  AccountContactSearch,
  undefined,
  undefined
);
